import { useReducer } from 'react'
import { Reservation, ReservationRecurrence } from 'models/Reservation'

const MODAL_NAMES = {
  CANCEL_RECURRENT_RESERVATION_MODAL: 'CANCEL_RECURRENT_RESERVATION_MODAL',
  CANCEL_NON_RECURRENT_RESERVATION_MODAL: 'CANCEL_NON_RECURRENT_RESERVATION_MODAL',
  CANCEL_RESERVATION_QUESTION_MODAL: 'CANCEL_RESERVATION_QUESTION_MODAL',
  CANCEL_RESERVATION_DAY_MODAL: 'CANCEL_RESERVATION_DAY_MODAL',
  RESCHEDULE_NON_RECURRENT_RESERVATION_MODAL: 'RESCHEDULE_NON_RECURRENT_RESERVATION_MODAL',
  RESCHEDULE_RESERVATION_QUESTION_MODAL: 'RESCHEDULE_RESERVATION_QUESTION_MODAL',
  RESCHEDULE_PER_DATE_RESERVATION_MODAL: 'RESCHEDULE_PER_DATE_RESERVATION_MODAL',
  RESCHEDULE_TOTAL_RESERVATION_MODAL: 'RESCHEDULE_TOTAL_RESERVATION_MODAL',
  UNDER_24_HOUR_WARNING_MODAL: 'UNDER_24_HOUR_WARNING_MODAL',
}

export type ModalState = {
  [key in keyof typeof MODAL_NAMES]: boolean
}

export type ModalActionType = {
  actionType?: 'cancelar' | 'remarcar'
}

export type CombinedModalState = ModalState & ModalActionType;

export type ModalNames = keyof typeof MODAL_NAMES

export type ModalAction = 
  | { type: 'OPEN_MODAL'; modalName: ModalNames; actionType?: 'cancelar' | 'remarcar' }
  | { type: 'CLOSE_MODAL'; modalName: ModalNames; }

interface UseReservationListItemHook {
  modalState: ModalState
  modalDispatch: React.Dispatch<ModalAction>
  handleClickCancelOption: () => void
  handleClickRescheduleOption: () => void
}

interface HandleModalParams {
  recurrence: ReservationRecurrence
  modalDispatch: React.Dispatch<ModalAction>
  modalName: ModalNames
  isUnder24Hours: boolean
  actionType?: 'cancelar' | 'remarcar'
}

const OPEN_MODAL = 'OPEN_MODAL'
const CLOSE_MODAL = 'CLOSE_MODAL'

const initialModalState: CombinedModalState = Object.keys(MODAL_NAMES)
  .reduce((state, modalName) => {
    state[modalName as ModalNames] = false
    return state
  }, { actionType: undefined } as CombinedModalState)

function modalReducer(state: CombinedModalState, action: ModalAction): CombinedModalState {
  switch (action.type) {
  case OPEN_MODAL:
    return {
      ...state,
      [action.modalName]: true,
      actionType: action.actionType,
    }
  case CLOSE_MODAL:
    return {
      ...state,
      [action.modalName]: false,
    }
  default:
    return state
  }
}

const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000

const openModal = (
  modalDispatch: React.Dispatch<ModalAction>, 
  modalName: ModalNames, 
  actionType: 'cancelar' | 'remarcar'
) => modalDispatch({ type: OPEN_MODAL, modalName, actionType })

const handleModal = ({ 
  recurrence, 
  modalDispatch, 
  modalName, 
  isUnder24Hours, 
  actionType 
}: HandleModalParams): void => {
  if (isUnder24Hours && recurrence === ReservationRecurrence.NON_RECURRENT) {
    return openModal(modalDispatch, 'UNDER_24_HOUR_WARNING_MODAL', actionType)
  }

  return openModal(modalDispatch, modalName, actionType)
}

const cancelModalMap = {
  [ReservationRecurrence.WEEKLY]: 'CANCEL_RESERVATION_QUESTION_MODAL',
  [ReservationRecurrence.NON_RECURRENT]: 'CANCEL_NON_RECURRENT_RESERVATION_MODAL'
}

export const useReservationListItem = (reservation: Reservation): UseReservationListItemHook => {
  const [modalState, modalDispatch] = useReducer(modalReducer, initialModalState)

  const isUnder24Hours = new Date(reservation.startDate).getTime() - new Date().getTime() < ONE_DAY_IN_MILLISECONDS

  const handleClickCancelOption = () => {
    const modalName = cancelModalMap[reservation.recurrence] as ModalNames
    return handleModal({
      recurrence: reservation.recurrence,
      modalDispatch,
      modalName,
      isUnder24Hours,
      actionType: 'cancelar'
    })
  }

  const handleClickRescheduleOption = () => {
    if (reservation.recurrence === ReservationRecurrence.WEEKLY) {
      return handleModal({
        recurrence: reservation.recurrence,
        modalDispatch,
        modalName: 'RESCHEDULE_RESERVATION_QUESTION_MODAL',
        isUnder24Hours: false,
        actionType: 'remarcar'
      })
    }

    return handleModal({
      recurrence: reservation.recurrence,
      modalDispatch,
      modalName: 'RESCHEDULE_NON_RECURRENT_RESERVATION_MODAL',
      isUnder24Hours,
      actionType: 'remarcar'
    })
  }

  return {
    modalState,
    modalDispatch,
    handleClickCancelOption,
    handleClickRescheduleOption,
  }
}